import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { SelectModule } from 'primeng/select';
import {
  BesharpTableHeaderDefinition,
  BesharpTableSize,
  BesharpTableActionDefinition,
} from '../../modules/besharp-table/component/besharp-table.component';
import { BesharpTableComponent } from '../../modules/besharp-table/component/besharp-table.component';
import { UserService, UserDto, UserTypeDto } from '../../services/user.service';
import { environment } from '../../../environment/environment';
import { countries, countryCodes } from '../signup-page/utils/countries-list';
import { fetchAuthSession } from 'aws-amplify/auth';

type UserData = UserDto & {
  id: string;
};

@Component({
  selector: 'app-user-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ToastModule,
    ButtonModule,
    ConfirmDialogModule,
    DialogModule,
    InputTextModule,
    PasswordModule,
    SelectModule,
    BesharpTableComponent,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss']
})
export class UserManagementComponent implements OnInit {

  private static readonly PROD_DISABLED_ROLES = ['software-developer', 'software-manager'];
  private allowedRolesToCreate: string[] | null = null;
  
  datasource: UserData[] = [];
  datasourceLength = 0;
  loading = false;
  pageRows = 10;
  currentPage = 0;
  orderColumn: string = 'email';
  orderDirection: 'asc' | 'desc' = 'asc';
  filters: { [key: string]: any } = {};
  searchValue: string = '';

  displayCreateUserDialog = false;
  creatingUser = false;
  loadingUserTypes = false;
  userTypes: UserTypeDto[] = [];
  
  createUserForm = {
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    surname: '',
    userType: '',
    phoneNumber: '',
    address: '',
    country: '',
  };

  get isFormValid(): boolean {
    const phoneNumberValid = !this.createUserForm.phoneNumber || this.isPhoneNumberValid;
    
    return !!(
      this.createUserForm.email &&
      this.createUserForm.password &&
      this.createUserForm.confirmPassword &&
      this.createUserForm.name &&
      this.createUserForm.surname &&
      this.createUserForm.userType &&
      this.createUserForm.country &&
      this.createUserForm.password.length >= 8 &&
      this.createUserForm.password === this.createUserForm.confirmPassword &&
      this.createUserForm.phoneNumber &&
      this.createUserForm.address &&
      phoneNumberValid
    );
  }

  get passwordsMatch(): boolean {
    if (!this.createUserForm.password || !this.createUserForm.confirmPassword) {
      return true;
    }
    return this.createUserForm.password === this.createUserForm.confirmPassword;
  }

  get isPasswordTooShort(): boolean {
    return this.createUserForm.password.length > 0 && this.createUserForm.password.length < 8;
  }

  get showPasswordError(): boolean {
    return !this.passwordsMatch && this.createUserForm.confirmPassword.length > 0;
  }

  get isPhoneNumberValid(): boolean {
    if (!this.createUserForm.phoneNumber || this.createUserForm.phoneNumber.trim() === '') {
      return true; 
    }
    const e164Regex = /^\+[1-9]\d{1,14}$/;
    return e164Regex.test(this.createUserForm.phoneNumber);
  }

  get showPhoneNumberError(): boolean {
    return this.createUserForm.phoneNumber.length > 0 && !this.isPhoneNumberValid;
  }

  eBesharpTableSize = BesharpTableSize;
  countries = countries;

  tableDefinition: BesharpTableHeaderDefinition[] = [
    {
      def: 'email',
      title: 'Email (Username)',
      sortable: true,
      columnFilter: true,
    },
    {
      def: 'userType',
      title: 'Type of User',
      sortable: true,
      columnFilter: true,
      columnFilterOptions: [
        { label: 'Technician', value: 'technician' },
        { label: 'Technician Admin', value: 'technician-admin' },
        { label: 'Software Developer', value: 'software-developer' },
        { label: 'Software Manager', value: 'software-manager' },
        { label: 'Global Admin', value: 'global-admin' },
      ],
    },
    {
      def: 'name',
      title: 'Name',
      sortable: true,
      columnFilter: true,
    },
    {
      def: 'surname',
      title: 'Surname',
      sortable: true,
      columnFilter: true,
    },
    {
      def: 'phoneNumber',
      title: 'Phone Number',
      sortable: true,
      columnFilter: true,
    },
    {
      def: 'address',
      title: 'Address',
      sortable: true,
      columnFilter: true,
    },
    {
      def: 'country',
      title: 'Country',
      sortable: true,
      columnFilter: true,
    },
    {
      def: 'accepted',
      title: 'Status',
      sortable: true,
      columnFilter: false,
      transformer: (cellData: boolean) => cellData ? 'Active' : 'Pending',
    },
  ];

  protected actionDefinition: BesharpTableActionDefinition[] = [
    {
      label: 'Approve',
      icon: 'pi pi-check',
      styleClass: 'success',
      callback: (user: UserData) => this.approveUser(user),
      active: (user: UserData) => !user.accepted,
    },
    {
      label: 'Delete',
      icon: 'pi pi-trash',
      styleClass: 'danger',
      callback: (user: UserData) => this.deleteUser(user),
    },
  ];

  protected globalFilterFields = ['email', 'userType', 'name', 'surname', 'phoneNumber', 'address', 'country'];

  constructor(
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.loadUsers();
    this.loadAllowedRoles();
  }

  private async loadAllowedRoles(): Promise<void> {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      if (idToken) {
        const payload = JSON.parse(atob(idToken.toString().split('.')[1]));
        const groups: string[] = payload['cognito:groups'] || [];
        if (groups.some(g => g.includes('global-admin'))) {
          this.allowedRolesToCreate = null;
        } else if (groups.some(g => g.includes('sw-manager'))) {
          this.allowedRolesToCreate = ['software-developer'];
        } else if (groups.some(g => g.includes('technician-admin'))) {
          this.allowedRolesToCreate = ['technician'];
        } else {
          this.allowedRolesToCreate = [];
        }
      }
    } catch (error) {
      console.error('Error loading user role:', error);
      this.allowedRolesToCreate = [];
    }
  }

  private loadUsers(): void {
    this.loading = true;
    this.datasource = [];
    const offset = this.currentPage * this.pageRows;
    this.userService.getUsers(this.pageRows, offset, this.orderColumn, this.orderDirection, this.filters).subscribe({
      next: (response) => {
        this.datasource = response.data.map(user => ({
          ...user,
          id: user.userId
        }));
        this.datasourceLength = response.total;
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to load users: ${error.message}`
        });
      }
    });
  }

  approveUser(user: UserData): void {
    this.confirmationService.confirm({
      message: `You're about to approve this user to use the platform, are you sure?`,
      header: 'Confirm Approval',
      icon: 'pi pi-question-circle',
      acceptButtonStyleClass: 'p-button-success',
      accept: () => {
        this.userService.acceptUser(user.userId).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'User Approved',
              detail: `User ${user.email} has been approved successfully.`,
            });
            this.loadUsers();
          },
          error: (error) => {
            console.error('Error approving user:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to approve user. Please try again.'
            });
          }
        });
      }
    });
  }

  deleteUser(user: UserData): void {
    this.confirmationService.confirm({
      message: `You're about to remove permanently this user from the platform, are you sure?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.userService.deleteUser(user.userId).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'User Deleted',
              detail: `User ${user.email} has been deleted successfully.`,
            });
            this.loadUsers();
          },
          error: (error) => {
            console.error('Error deleting user:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete user. Please try again.'
            });
          }
        });
      }
    });
  }

  onPageChange(event: any): void {
    this.currentPage = event.first / event.rows;
    this.pageRows = event.rows;
    this.loadUsers();
  }

  sortChangedEvent(event: any): void {
    let field = event.field;
    if (field === 'userType') {
      field = 'user_type_name';
    }
    if (field === 'phoneNumber') {
      field = 'phone_number';
    }
    this.orderColumn = field;
    this.orderDirection = event.order === 1 ? 'asc' : 'desc';
    this.currentPage = 0;
    this.loadUsers();
  }

  onColumnFilter(event: any): void {
    const rawFilters = event.filters || {};
    const apiFilters: { [key: string]: any } = {};
    for (const [key, filterArray] of Object.entries(rawFilters)) {
      if (Array.isArray(filterArray) && filterArray.length > 0) {
        const filterObj = filterArray[0];
        const value = filterObj.value;
        if (value === null || value === undefined || value === '') {
          continue;
        }
        if (key === 'userType') {
          apiFilters['userType'] = value;
        } else if (key === 'phoneNumber') {
          apiFilters['phone_number'] = value;
        } else if (key === 'accepted') {
          continue;
        } else {
          apiFilters[key] = value;
        }
      }
    }
    
    this.filters = apiFilters;
    this.currentPage = 0;
    this.loadUsers();
  }

  onGlobalSearch(searchValue: string): void {
    this.searchValue = searchValue;
    this.filters = { ...this.filters, search: searchValue || null };
    this.currentPage = 0;
    this.loadUsers();
  }

  clearFilters(): void {
    this.orderColumn = 'email';
    this.orderDirection = 'asc';
    this.filters = {};
    this.searchValue = '';
    this.currentPage = 0;
    this.loadUsers();
  }

  getUserTypeNameById(id: string): string {
    const userType = this.userTypes.find(type => type.id === id);
    return userType ? userType.name : '';
  }

  showCreateUserModal(): void {
    this.displayCreateUserDialog = true;
    this.loadUserTypes();
  }

  loadUserTypes(): void {
    this.loadingUserTypes = true;
    this.userService.listUserTypes().subscribe({
      next: (types) => {
        let filtered = environment.production
          ? types.filter(t => !UserManagementComponent.PROD_DISABLED_ROLES.includes(t.name))
          : types;
        if (this.allowedRolesToCreate !== null) {
          filtered = filtered.filter(t => this.allowedRolesToCreate!.includes(t.name));
        }
        this.userTypes = filtered;
        this.loadingUserTypes = false;
      },
      error: (error) => {
        console.error('Error loading user types:', error);
        this.loadingUserTypes = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load user types.'
        });
      }
    });
  }

  resetCreateUserForm(): void {
    this.createUserForm = {
      email: '',
      password: '',
      confirmPassword: '',
      name: '',
      surname: '',
      userType: '',
      phoneNumber: '',
      address: '',
      country: '',
    };
  }

  onCountryChange(country: string): void {
    if (!country) return;
    const countryCode = countryCodes[country];
    if (!countryCode) return;
    const currentPhone = this.createUserForm.phoneNumber || '';
    if (currentPhone && currentPhone.startsWith('+')) return;
    if (currentPhone) {
      const cleanPhone = currentPhone.replace(/^0+/, '').replace(/^\D+/, '');
      if (cleanPhone) {
        this.createUserForm.phoneNumber = `${countryCode}${cleanPhone}`;
      }
    } else {
      this.createUserForm.phoneNumber = countryCode;
    }
  }

  onPhoneNumberInput(): void {
    const currentPhone = this.createUserForm.phoneNumber || '';
    const selectedCountry = this.createUserForm.country;
    if (selectedCountry && currentPhone && !currentPhone.startsWith('+') && /^\d/.test(currentPhone)) {
      const countryCode = countryCodes[selectedCountry];
      if (countryCode) {
        this.createUserForm.phoneNumber = `${countryCode}${currentPhone.replace(/^0+/, '')}`;
      }
    }
  }

  createUser(): void {
    if (!this.createUserForm.email || !this.createUserForm.password || !this.createUserForm.name || 
        !this.createUserForm.surname || !this.createUserForm.userType || !this.createUserForm.country) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields (marked with *).'
      });
      return;
    }

    if (this.createUserForm.password.length < 8) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Password must be at least 8 characters long.'
      });
      return;
    }

    if (this.createUserForm.password !== this.createUserForm.confirmPassword) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Passwords do not match. Please re-enter your password.'
      });
      return;
    }

    if (this.createUserForm.phoneNumber && !this.isPhoneNumberValid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Phone number must be in this format: +393331234567'
      });
      return;
    }

    this.creatingUser = true;

    const requestBody = {
      email: this.createUserForm.email,
      password: this.createUserForm.password,
      name: this.createUserForm.name,
      surname: this.createUserForm.surname,
      userType: this.createUserForm.userType,
      phoneNumber: this.createUserForm.phoneNumber || '',
      address: this.createUserForm.address || '',
      country: this.createUserForm.country,
    };

    this.userService.createUser(requestBody).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'User Created',
          detail: `User ${this.createUserForm.email} has been created successfully but is pending approval. To enable it, approve it from the user list.`,
          life: 10000
        });
        this.displayCreateUserDialog = false;
        this.resetCreateUserForm();
        this.creatingUser = false;
        this.loadUsers();
      },
      error: (error) => {
        console.error('Error creating user:', error);
        this.creatingUser = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || 'Failed to create user. Please try again.'
        });
      }
    });
  }
}
